import React, { forwardRef, useEffect, useImperativeHandle, useRef } from "react";
import { Box } from "@mui/material";
import { CW } from "./cwUi";

/**
 * P4.1-lite — small rich-text box (bold / italic / list) used by the coach
 * notebook and the feedback composer. Plain contentEditable: no TinyMCE,
 * keeps the HTML as typed. Only used on the new coach workspace screens.
 *
 * ref: { getHtml(), setHtml(html), insertText(text), insertHtml(html), focus() }
 */

const toolButton = {
  border: `1px solid ${CW.line}`,
  background: "#fff",
  borderRadius: 6,
  minWidth: 30,
  height: 28,
  cursor: "pointer",
  fontSize: 13,
  color: CW.ink,
  padding: "0 6px",
};

const escapeHtml = (v) =>
  String(v == null ? "" : v).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

const LightEditor = forwardRef(function LightEditor(
  { initialHtml = "", onChange, placeholder = "", minHeight = 140, maxHeight = 380, readOnly = false, toolbarExtra = null, testId },
  ref
) {
  const boxRef = useRef(null);
  const savedRange = useRef(null);
  // initialHtml only seeds the box once (it is uncontrolled while typing).
  const initialRef = useRef(initialHtml);

  useEffect(() => {
    if (boxRef.current) boxRef.current.innerHTML = initialRef.current || "";
  }, []);

  const emit = () => {
    if (onChange && boxRef.current) onChange(boxRef.current.innerHTML);
  };

  const rememberRange = () => {
    const sel = window.getSelection && window.getSelection();
    if (sel && sel.rangeCount && boxRef.current && boxRef.current.contains(sel.anchorNode)) {
      savedRange.current = sel.getRangeAt(0).cloneRange();
    }
  };

  /** Puts HTML at the caret (last caret in the box), else at the end. */
  const insertHtml = (html) => {
    const box = boxRef.current;
    if (!box) return;
    box.focus();
    const sel = window.getSelection();
    let range = savedRange.current;
    if (!range || !box.contains(range.startContainer)) {
      range = document.createRange();
      range.selectNodeContents(box);
      range.collapse(false);
    }
    sel.removeAllRanges();
    sel.addRange(range);
    try {
      if (!document.execCommand("insertHTML", false, html)) throw new Error("no insertHTML");
    } catch (_err) {
      const frag = range.createContextualFragment(html);
      range.insertNode(frag);
    }
    rememberRange();
    emit();
  };

  useImperativeHandle(ref, () => ({
    getHtml: () => (boxRef.current ? boxRef.current.innerHTML : ""),
    setHtml: (html) => {
      if (boxRef.current) boxRef.current.innerHTML = html || "";
    },
    insertHtml,
    insertText: (text) => insertHtml(escapeHtml(text).replace(/\n/g, "<br>")),
    focus: () => boxRef.current && boxRef.current.focus(),
  }));

  const exec = (command) => {
    if (boxRef.current) boxRef.current.focus();
    try {
      document.execCommand(command, false, null);
    } catch (_err) {
      /* old browsers */
    }
    emit();
  };

  return (
    <Box>
      {!readOnly ? (
        <Box sx={{ display: "flex", gap: 0.75, mb: 1, flexWrap: "wrap", alignItems: "center" }}>
          <button type="button" style={{ ...toolButton, fontWeight: 800 }} onMouseDown={(e) => e.preventDefault()} onClick={() => exec("bold")} title="Bold">
            B
          </button>
          <button type="button" style={{ ...toolButton, fontStyle: "italic" }} onMouseDown={(e) => e.preventDefault()} onClick={() => exec("italic")} title="Italic">
            I
          </button>
          <button type="button" style={toolButton} onMouseDown={(e) => e.preventDefault()} onClick={() => exec("insertUnorderedList")} title="List">
            •
          </button>
          {toolbarExtra}
        </Box>
      ) : null}
      <Box
        ref={boxRef}
        contentEditable={!readOnly}
        suppressContentEditableWarning
        onInput={emit}
        onKeyUp={rememberRange}
        onMouseUp={rememberRange}
        onBlur={rememberRange}
        data-testid={testId}
        data-placeholder={placeholder}
        sx={{
          minHeight,
          maxHeight,
          overflowY: "auto",
          border: `1px solid ${CW.line}`,
          borderRadius: "10px",
          p: 1.5,
          fontSize: 14,
          lineHeight: 1.55,
          color: CW.ink,
          outline: "none",
          bgcolor: readOnly ? CW.soft : "#fcfdfd",
          wordBreak: "break-word",
          "&:focus": { borderColor: CW.primary },
          "& p": { m: 0 },
          "& blockquote": { m: "4px 0", pl: 1.25, borderLeft: `3px solid ${CW.line}`, color: CW.muted },
          "&:empty:before": { content: "attr(data-placeholder)", color: CW.muted },
        }}
      />
    </Box>
  );
});

export default LightEditor;
